import useHasAccess from "@hooks/useHasAccess"
import useSlugExtractor from "@hooks/useSlugExtractor"

import { Table, ViewBar, ViewMenu } from "@organisms"

import { BILLS_ROUTES } from "@routes/bills"

import { PERMISSION_LEVEL } from "@constants/permissionLevel"
import {
    BILLS_LIST_CONTROLS_ACTIVE_TAB,
    BILLS_LIST_CONTROLS_KEYWORD_SEARCH,
    BILLS_LIST_CONTROLS_PHASE,
} from "@constants/storage"

import { BillTableViewTabs } from "./BillTableViewTabs"

export default function BillTableView() {
    const [createRoute] = useSlugExtractor([BILLS_ROUTES.CREATE])

    const { hasPermission } = useHasAccess()

    return (
        <Table.Provider
            activePhaseStorageKey={BILLS_LIST_CONTROLS_PHASE}
            activeTabStorageKey={BILLS_LIST_CONTROLS_ACTIVE_TAB}
            searchKeywordsStorageKey={BILLS_LIST_CONTROLS_KEYWORD_SEARCH}
            listTitle="Bills"
            objectType="bill"
            objectIndexName="Bill"
            objectName="Bill"
            defaultActivePhase="open"
            objectCreateRoute={createRoute}
            isRowClickable={hasPermission("bills_view_permission", PERMISSION_LEVEL.FULL)}
            isEmptyStateCreateButtonVisible={hasPermission("bills_create_permission", PERMISSION_LEVEL.RESTRICTED)}
        >
            <ViewBar.Root>
                <ViewBar.Start>
                    <ViewBar.Phases>
                        <Table.Phase phase="open" title="Open" />
                        <Table.Phase phase="closed" title="Closed" />
                    </ViewBar.Phases>
                    <ViewBar.VerticalDivider />
                    <BillTableViewTabs />
                </ViewBar.Start>
                <ViewBar.End>
                    <Table.SearchInput />
                    <ViewMenu />
                </ViewBar.End>
            </ViewBar.Root>
            <Table.Table>
                <Table.Column<Bill>
                    accessorKey="id"
                    header="ID"
                    size={144}
                    cell={({ row }) => <Table.Cells.IDCell id={row.original.custom_id || row.original.id} />}
                    enableSorting={true}
                    enablePinning={true}
                    dataType="alphabetical"
                />
                <Table.Column<Bill>
                    accessorKey="status_label"
                    header="Status"
                    size={176}
                    cell={({ getValue }) => <Table.Cells.StatusCell objectType="bill" status={getValue()} />}
                    enableSorting={true}
                    enablePinning={false}
                    dataType="other"
                    loadingType="badge"
                />
                <Table.Column<Bill>
                    accessorKey="state_label"
                    header="State"
                    size={176}
                    cell={({ getValue }) => <Table.Cells.BadgesCell badges={[getValue()]} />}
                    enableSorting={true}
                    enablePinning={false}
                    dataType="other"
                    loadingType="badge"
                />
                <Table.Column<Bill>
                    accessorKey="labels"
                    header="Labels"
                    size={240}
                    cell={({ getValue }) => (
                        <Table.Cells.BadgesCell
                            badges={(getValue() as BillLabel[])?.map((label: BillLabel) => label.name)}
                        />
                    )}
                    enableSorting={false}
                    enablePinning={false}
                    dataType="other"
                    loadingType="badge"
                />
                <Table.Column<Bill>
                    accessorKey="subtotal"
                    header="Total"
                    size={144}
                    cell={({ getValue }) => <Table.Cells.NumericalDataCell value={getValue()} type="currency" />}
                    enableSorting={true}
                    enablePinning={false}
                    dataType="numerical"
                    alignment="right"
                />
                <Table.Column<Bill>
                    accessorKey="vendor_name"
                    header="Billing Vendor"
                    size={288}
                    cell={({ getValue }) => <Table.Cells.TextCell content={getValue()} />}
                    enableSorting={true}
                    enablePinning={true}
                    dataType="alphabetical"
                />
                <Table.Column<Bill>
                    accessorKey="vendor_invoice_id"
                    header="Vendor Invoice ID"
                    size={200}
                    cell={({ getValue }) => <Table.Cells.IDCell id={getValue()} />}
                    enableSorting={true}
                    enablePinning={true}
                    dataType="alphabetical"
                />
                <Table.Column<Bill>
                    accessorKey="date_due"
                    header="Due Date"
                    size={144}
                    cell={({ getValue }) => <Table.Cells.DateTimeCell value={getValue()} />}
                    enableSorting={true}
                    enablePinning={false}
                    dataType="timing"
                />
                <Table.Column<Bill>
                    accessorKey="last_status_transition_time"
                    header="Status Changed"
                    size={160}
                    cell={({ getValue }) => <Table.Cells.DateTimeCell value={getValue()} />}
                    enableSorting={false}
                    enablePinning={false}
                    dataType="timing"
                />
                <Table.Column<Bill>
                    accessorKey="last_modified_at"
                    header="Last Modified"
                    size={144}
                    cell={({ getValue }) => <Table.Cells.DateTimeCell value={getValue()} />}
                    enableSorting={true}
                    enablePinning={false}
                    dataType="timing"
                />
                <Table.Column<Bill>
                    accessorKey="last_received_at"
                    header="Received"
                    size={144}
                    cell={({ getValue }) => <Table.Cells.DateTimeCell value={getValue()} />}
                    enableSorting={true}
                    enablePinning={false}
                    dataType="timing"
                />
                <Table.Column<Bill>
                    accessorKey="last_paid_at"
                    header="Paid"
                    size={144}
                    cell={({ getValue }) => <Table.Cells.DateTimeCell value={getValue()} />}
                    enableSorting={true}
                    enablePinning={false}
                    dataType="timing"
                />
                <Table.Column<Bill>
                    accessorKey="last_voided_at"
                    header="Voided"
                    size={144}
                    cell={({ getValue }) => <Table.Cells.DateTimeCell value={getValue()} />}
                    enableSorting={true}
                    enablePinning={false}
                    dataType="timing"
                />
            </Table.Table>
        </Table.Provider>
    )
}
